// Turns a getUserMedia refusal into a status line that says how to recover.
// Browsers report the same underlying problem under different names, so the
// failure is classified first and the wording follows the user's browser and
// operating system.

import {
    isChrome,
    isChromium,
    isEdge,
    isFirefox,
    isMacOs,
    isSafari,
    isWindows
} from 'react-device-detect';

export type MicrophoneFailure =
    | 'dismissed'
    | 'site-blocked'
    | 'system-blocked'
    | 'not-found'
    | 'unreadable'
    | 'other';

type BrowserName = 'Chrome' | 'Edge' | 'Firefox' | 'Safari';
type Platform = 'mac' | 'windows' | 'other';

// Edge is checked first: its Chromium build also passes the Chrome checks.
const browser: BrowserName | null = isEdge
    ? 'Edge'
    : isFirefox
      ? 'Firefox'
      : isChrome || isChromium
        ? 'Chrome'
        : isSafari
          ? 'Safari'
          : null;

const platform: Platform = isMacOs ? 'mac' : isWindows ? 'windows' : 'other';

// Without a microphone grant the browser still lists one placeholder device
// per kind that exists, so any audioinput entry means hardware is present.
const hasAudioInputDevice = async (): Promise<boolean> => {
    if (typeof navigator.mediaDevices?.enumerateDevices !== 'function') {
        return false;
    }
    try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        return devices.some(device => device.kind === 'audioinput');
    } catch {
        return false;
    }
};

export const classifyMicrophoneFailure = async (
    error: unknown
): Promise<MicrophoneFailure> => {
    if (!(error instanceof DOMException)) {
        return 'other';
    }

    const message = error.message.toLowerCase();
    switch (error.name) {
        case 'NotAllowedError':
        case 'PermissionDeniedError':
            if (message.includes('dismiss')) {
                return 'dismissed';
            }
            // Chrome and Edge: "Permission denied by system" when the
            // operating system's privacy setting blocks the browser.
            return message.includes('system')
                ? 'system-blocked'
                : 'site-blocked';
        case 'NotFoundError':
        case 'DevicesNotFoundError':
            // Firefox on macOS reports a microphone the OS will not release
            // as NotFoundError, even after the site prompt was allowed.
            return (await hasAudioInputDevice())
                ? 'system-blocked'
                : 'not-found';
        case 'NotReadableError':
        case 'TrackStartError':
        case 'AbortError':
            return 'unreadable';
        default:
            return 'other';
    }
};

const siteBlockedMessage = () => {
    switch (browser) {
        case 'Chrome':
        case 'Edge':
            return 'Microphone access is blocked for this site. Click the site settings icon at the left of the address bar, allow Microphone, then try again.';
        case 'Firefox':
            return 'Microphone access is blocked for this site. Click the crossed-out microphone in the address bar, clear the block, then try again.';
        case 'Safari':
            return 'Microphone access is blocked for this site. Choose Safari > Settings for this website, set Microphone to Allow, then try again.';
        default:
            return "Microphone access is blocked for this site. Allow the microphone in the browser's site settings, then try again.";
    }
};

const systemBlockedMessage = () => {
    const app = browser ?? 'your browser';
    const subject = browser ?? 'Your browser';
    switch (platform) {
        case 'mac':
            return `${subject} could not open the microphone. macOS may be blocking it: open System Settings > Privacy & Security > Microphone, turn on ${app}, then quit and reopen ${app}.`;
        case 'windows':
            return `${subject} could not open the microphone. Windows may be blocking it: open Settings > Privacy & security > Microphone, turn on microphone access for apps and desktop apps, then try again.`;
        default:
            return `${subject} could not open the microphone. Check that your device's privacy settings let ${app} use it, then try again.`;
    }
};

export const getMicrophoneFailureMessage = (
    failure: MicrophoneFailure,
    error: unknown
): string => {
    switch (failure) {
        case 'dismissed':
            return 'The microphone prompt was closed without an answer. Try again, and if no prompt appears, allow the microphone in the site settings in the address bar.';
        case 'site-blocked':
            return siteBlockedMessage();
        case 'system-blocked':
            return systemBlockedMessage();
        case 'not-found':
            return 'No microphone was found. Connect a microphone, then try again.';
        case 'unreadable':
            return 'The microphone could not be started. Close other apps that may be using it, then try again.';
        default: {
            const message = error instanceof Error ? error.message : '';
            return message || 'Unable to access the microphone.';
        }
    }
};
